import { Loader } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { HistoryIcon } from "@/components/icons/action-icons";
import { Badge } from "@/components/ui/badge";
import { statusToVariant } from "@/components/ui/badge/statusToVariant";
import type { IDocumentVersion } from "@/types/document";
import { cn } from "@/utils/cn";
import { formatDate, formatDateTime, formatPerson } from "@/utils/helper";
import DocumentSidePreview from "./DocumentSidePreview";

interface DocumentVersionHistoryProps {
  /** Newest first, as the API returns them. */
  versions?: IDocumentVersion[];
  isLoading?: boolean;
  /** Drop the top rule + spacing when the list is the only thing on screen
   *  (inside a dialog) rather than a section under the attachments. */
  bare?: boolean;
}

const describeVersion = (v: IDocumentVersion, t: TFunction) => {
  if (v.version === 1) return t("documents:versions.initial_upload");
  if (v.action === "submitted") return t("documents:versions.submitted_by_employee");
  return t("documents:versions.changed_by_hr");
};

const REVIEW_LABEL_KEY: Record<NonNullable<IDocumentVersion["reviewStatus"]>, string> = {
  pending: "documents:versions.pending_review",
  approved: "documents:versions.approved",
  rejected: "documents:versions.rejected",
  superseded: "documents:versions.superseded",
};

/**
 * Every file set a document has carried: the first upload, HR file swaps and
 * employee renewals, each with its own dates and files. The replaced files
 * stay downloadable — that is the point of the list.
 */
const DocumentVersionHistory = ({
  versions = [],
  isLoading,
  bare,
}: DocumentVersionHistoryProps) => {
  const { t } = useTranslation(["documents", "common"]);
  const now = Date.now();
  // The version the document carries: the newest one that took effect. An
  // employee's pending, rejected or superseded submission never did.
  const currentVersion = versions.find(
    (v) => !v.reviewStatus || v.reviewStatus === "approved",
  )?.version;

  return (
    <div className={cn(!bare && "mt-6 pt-6 border-t border-border")}>
      {!bare && (
        <div className="flex items-center gap-2 mb-4">
          <HistoryIcon className="w-3.5 h-3.5 text-muted-foreground" />
          <h5 className="font-medium">{t("documents:versions.title")}</h5>
          {versions.length > 0 && (
            <span className="text-[11px] font-semibold text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
              {versions.length}
            </span>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-6">
          <Loader className="animate-spin text-primary" size={20} />
        </div>
      ) : versions.length === 0 ? (
        <p className="text-sm text-muted-foreground px-4 py-6 text-center border border-dashed border-border rounded-sm">
          {t("documents:versions.empty")}
        </p>
      ) : (
        <ol className="flex flex-col gap-3">
          {versions.map((v) => {
            const files = [
              { label: t("documents:front_side"), url: v.frontSideDocumentUrl },
              { label: t("documents:back_side"), url: v.backSideDocumentUrl },
            ].filter((f): f is { label: string; url: string } => Boolean(f.url));
            const lapsed =
              !!v.expiryDate && new Date(v.expiryDate).getTime() < now;

            return (
              <li
                key={v.version}
                className="flex flex-col gap-3 p-3 border border-border rounded-sm"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">
                      {t("documents:versions.version_n", { version: v.version })}
                    </span>
                    {v.version === currentVersion ? (
                      <Badge variant="primary">{t("documents:versions.current")}</Badge>
                    ) : (
                      v.reviewStatus && (
                        <Badge variant={statusToVariant(v.reviewStatus)}>
                          {t(REVIEW_LABEL_KEY[v.reviewStatus])}
                        </Badge>
                      )
                    )}
                    <span className="text-xs text-muted-foreground">
                      {describeVersion(v, t)}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatDateTime(v.createdAt)}
                    {v.uploader && ` · ${formatPerson(v.uploader)}`}
                  </span>
                </div>

                {(v.issuingDate || v.expiryDate) && (
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
                    {v.issuingDate && (
                      <span className="text-muted-foreground">
                        {t("documents:versions.issued")}{" "}
                        <span className="text-foreground font-medium">
                          {formatDate(v.issuingDate)}
                        </span>
                      </span>
                    )}
                    {v.expiryDate && (
                      <span className="text-muted-foreground">
                        {lapsed
                          ? t("documents:versions.expired")
                          : t("documents:versions.expires")}{" "}
                        <span
                          className={cn(
                            "font-medium",
                            lapsed ? "text-danger" : "text-foreground",
                          )}
                        >
                          {formatDate(v.expiryDate)}
                        </span>
                      </span>
                    )}
                  </div>
                )}

                {files.length > 0 ? (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {files.map((f) => (
                      <DocumentSidePreview
                        key={f.label}
                        label={f.label}
                        url={f.url}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {t("documents:versions.no_file_attached")}
                  </p>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default DocumentVersionHistory;
